import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Badge,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  MarkEmailRead as ReadIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import notificationsService, { Notification, NotificationStats } from '../../services/notifications.service';

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  // Push broadcast (anunciar nova versão, etc.)
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushApp, setPushApp] = useState<'pagali' | 'pagali_shop' | 'pass' | 'pos' | 'todas'>('pagali');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushInfo, setPushInfo] = useState<string | null>(null);

  const handlePushCount = async () => {
    setPushBusy(true); setPushInfo(null);
    try {
      const r = await notificationsService.pushBroadcast({ title: pushTitle || '—', body: pushBody || '—', app: pushApp, dryRun: true });
      setPushInfo(`${r.data.total} dispositivos registados para receber este push.`);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao contar dispositivos');
    } finally { setPushBusy(false); }
  };

  const handlePushSend = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) { setError('Título e mensagem são obrigatórios'); return; }
    if (!window.confirm(`Enviar push a TODOS os dispositivos (${pushApp})?\n\n${pushTitle}\n${pushBody}`)) return;
    setPushBusy(true); setPushInfo(null);
    try {
      const r = await notificationsService.pushBroadcast({ title: pushTitle.trim(), body: pushBody.trim(), app: pushApp });
      setSuccessMessage(`Push enviado: ${r.data.sent}/${r.data.total} entregues${r.data.failed ? `, ${r.data.failed} falhados` : ''}`);
      setPushInfo(null);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao enviar push');
    } finally { setPushBusy(false); }
  };

  const [stats, setStats] = useState<NotificationStats>({
    total: 0,
    unread: 0,
    read: 0,
    highPriority: 0,
  });

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Determine status filter based on tab
      let statusFilter: string | undefined;
      if (tabValue === 0) statusFilter = 'unread';
      else if (tabValue === 1) statusFilter = 'read';

      const response = await notificationsService.getNotifications({
        status: statusFilter,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        search: searchTerm || undefined,
        limit: 100,
      });

      if (response.success) {
        setNotifications(response.data || []);
        // Check if setup is needed
        if ((response as any).needsSetup) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      }
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError(t('notifications.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [tabValue, typeFilter, searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await notificationsService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [fetchNotifications, fetchStats]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'success':
        return <SuccessIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'error.main';
      case 'warning':
        return 'warning.main';
      case 'success':
        return 'success.main';
      default:
        return 'info.main';
    }
  };

  const getPriorityChip = (priority: string) => {
    const config: Record<string, { color: 'error' | 'warning' | 'default'; label: string }> = {
      high: { color: 'error', label: t('notifications.priorityHigh') },
      medium: { color: 'warning', label: t('notifications.priorityMedium') },
      low: { color: 'default', label: t('notifications.priorityLow') },
    };
    const { color, label } = config[priority] || config.low;
    return <Chip size="small" color={color} label={label} />;
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications(notifications.map(n =>
        n.id === notificationId
          ? { ...n, status: 'read' as const, readAt: new Date().toISOString().replace('T', ' ').substring(0, 19) }
          : n
      ));
      fetchStats();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      fetchNotifications();
      fetchStats();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDelete = async (notificationId: string) => {
    if (window.confirm(t('notifications.confirmDelete'))) {
      try {
        await notificationsService.deleteNotification(notificationId);
        setNotifications(notifications.filter(n => n.id !== notificationId));
        fetchStats();
      } catch (err) {
        console.error('Error deleting notification:', err);
      }
    }
  };

  const handleViewDetails = (notification: Notification) => {
    setSelectedNotification(notification);
    setDetailDialog(true);
    if (notification.status === 'unread') {
      handleMarkAsRead(notification.id);
    }
  };

  const handleRefresh = () => {
    fetchNotifications();
    fetchStats();
  };

  const handleSetupTable = async () => {
    setSettingUp(true);
    setError(null);
    try {
      const response = await notificationsService.setupTable();
      if (response.success) {
        setSuccessMessage(response.message);
        fetchNotifications();
        fetchStats();
      }
    } catch (err: any) {
      console.error('Error setting up table:', err);
      setError(t('notifications.errorSetup'));
    } finally {
      setSettingUp(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('notifications.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" onClick={handleMarkAllAsRead} disabled={stats.unread === 0}>
            {t('notifications.markAllRead')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Push broadcast para a app */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Push para a App
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Notificação enviada a todos os dispositivos com a app instalada (ex.: anunciar nova versão).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField size="small" label="Título" value={pushTitle} onChange={e => setPushTitle(e.target.value)} sx={{ minWidth: 260 }} inputProps={{ maxLength: 60 }} />
            <TextField size="small" label="Mensagem" value={pushBody} onChange={e => setPushBody(e.target.value)} sx={{ minWidth: 380, flex: 1 }} inputProps={{ maxLength: 180 }} multiline />
            <TextField size="small" select label="App" value={pushApp} onChange={e => setPushApp(e.target.value as any)} sx={{ minWidth: 150 }} SelectProps={{ native: true }}>
              <option value="pagali">Pagali</option>
              <option value="pagali_shop">Pagali Shop</option>
              <option value="pass">Pass (eventos)</option>
              <option value="todas">Todas as apps</option>
              <option value="pos">Terminais POS</option>
            </TextField>
            <Button variant="outlined" onClick={handlePushCount} disabled={pushBusy}>Contar destinatários</Button>
            <Button variant="contained" onClick={handlePushSend} disabled={pushBusy || !pushTitle.trim() || !pushBody.trim()}>
              {pushBusy ? 'A enviar…' : 'Enviar push'}
            </Button>
          </Box>
          {pushInfo && (
            <Alert severity="info" sx={{ mt: 2 }} onClose={() => setPushInfo(null)}>{pushInfo}</Alert>
          )}
        </CardContent>
      </Card>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Setup Alert - shown when table needs setup */}
      {!loading && needsSetup && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={settingUp ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
              onClick={handleSetupTable}
              disabled={settingUp}
            >
              {settingUp ? t('notifications.settingUp') : t('notifications.setupTable')}
            </Button>
          }
        >
          {t('notifications.setupNeeded')}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={stats.unread} color="error">
                  <NotificationIcon color="warning" />
                </Badge>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.unread}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('notifications.unread')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.highPriority}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('notifications.highPriority')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('notifications.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchNotifications()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('notifications.typeLabel')}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="info">{t('notifications.typeInfo')}</MenuItem>
                <MenuItem value="warning">{t('notifications.typeWarning')}</MenuItem>
                <MenuItem value="error">{t('notifications.typeError')}</MenuItem>
                <MenuItem value="success">{t('notifications.typeSuccess')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="fullWidth"
        >
          <Tab
            icon={<Badge badgeContent={stats.unread} color="error"><NotificationIcon /></Badge>}
            label={t('notifications.unread')}
            iconPosition="start"
          />
          <Tab icon={<ReadIcon />} label={t('notifications.read')} iconPosition="start" />
          <Tab icon={<NotificationIcon />} label={t('notifications.allTab')} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Notifications List */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary={t('notifications.noNotifications')}
                  secondary={t('notifications.noMatch')}
                  sx={{ textAlign: 'center', py: 4 }}
                />
              </ListItem>
            ) : (
              notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    sx={{
                      bgcolor: notification.status === 'unread' ? 'action.hover' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                    }}
                    onClick={() => handleViewDetails(notification)}
                    secondaryAction={
                      <Box>
                        {notification.status === 'unread' && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification.id);
                            }}
                            title={t('notifications.markAsRead')}
                          >
                            <ReadIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notification.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getTypeColor(notification.type) }}>
                        {getTypeIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight={notification.status === 'unread' ? 600 : 400}>
                            {notification.title}
                          </Typography>
                          {getPriorityChip(notification.priority)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              <ScheduleIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                              {notification.createdAt}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              <GroupIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                              {notification.recipient}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedNotification && getTypeIcon(selectedNotification.type)}
          {t('notifications.details')}
        </DialogTitle>
        <DialogContent dividers>
          {selectedNotification && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedNotification.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedNotification.message}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('notifications.typeLabel')}</Typography>
                  <Typography variant="body2">{selectedNotification.type}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('notifications.priority')}</Typography>
                  <Box>{getPriorityChip(selectedNotification.priority)}</Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('notifications.recipient')}</Typography>
                  <Typography variant="body2">{selectedNotification.recipient}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                  <Typography variant="body2">{selectedNotification.status === 'unread' ? t('notifications.statusUnread') : t('notifications.statusRead')}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">{t('notifications.createdAt')}</Typography>
                  <Typography variant="body2">{selectedNotification.createdAt}</Typography>
                </Grid>
                {selectedNotification.readAt && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">{t('notifications.readAt')}</Typography>
                    <Typography variant="body2">{selectedNotification.readAt}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notifications;
